import React from 'react'
import { ViewerLoader } from '../viewer'
import {
  ApplicationArchitecture,
  ApplicationArchitectureLoader
} from './application-architecture-loader'
import {
  ArchitectureAssociatedApplication,
  ArchitectureAssociatedApplicationsLoader
} from './architecture-associated-applications-loader'
import {
  ApplicationArchitectureComponentsLoader,
  ApplicationArchitectureComponent
} from './application-architecture-components-loader'
import { Role } from '@contrast-security-inc/base-types'
import {
  AggregationOptions,
  Aggregations,
  AggregationsLoader
} from '../aggregations'
import { CustomApplicationAggregation } from '../applications-grid'
import { MetadataAggregationsLoader } from './metadata-aggregations-loader'
import gql from 'graphql-tag'

const getArchitectureAssociatedAppAggregationsQuery = (id) => gql`
  query ArchitectureAssociatedAppAggregations(
    $organizationID: ID!
    $apps: [String]
    $tags: [String]
    $technologies: [String]
    $languages: [String]
    $servers: [String]
    $environments: [String]
    $customMetadata: [CustomMetadataFilter]
  ) {
    architectureAssociatedApplicationAggregations(
      organizationID: $organizationID
      applicationID: "${id}"
      filters: {
        apps: $apps
        tags: $tags
        technologies: $technologies
        languages: $languages
        servers: $servers
        environments: $environments
        customMetadata: $customMetadata
      }
    ) {
      servers {
        count
        key {
          name
          id
        }
      }
      languages {
        count
        key {
          id
          label
        }
      }
      technologies {
        count
        key
      }
      tags {
        count
        key
      }
      environments {
        count
        key {
          id
          label
        }
      }
    }
  }
`

const getCustomArchitectureAssociatedApplicationAggregationsQuery = (id) => gql`
  query CustomArchitectureAssociatedApplicationAggregations(
    $organizationID: ID!
    $apps: [String]
    $tags: [String]
    $technologies: [String]
    $languages: [String]
    $servers: [String]
    $environments: [String]
    $customMetadata: [CustomMetadataFilter]
  ) {
    customArchitectureAssociatedApplicationAggregations(
      organizationID: $organizationID
      applicationID: "${id}"
      filters: {
      apps: $apps
      tags: $tags
      technologies: $technologies
      languages: $languages
      servers: $servers
      environments: $environments
      customMetadata: $customMetadata
      }
    ) {
      id
      fieldType
      label
      buckets {
        count
          key {
            id
            value
            subfieldValues {
              fieldType
              value
            }
          }
      }
    }
  }`

interface ChildProps<TAggregationCategory extends string> {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly organizationType: string
  readonly applicationArchitecture?: ApplicationArchitecture
  readonly applicationArchitectureComponents: ApplicationArchitectureComponent[]
  readonly architectureAssociatedApplications: ArchitectureAssociatedApplication[]
  readonly aggregations?: Aggregations<TAggregationCategory>
  readonly metadataAggregations?: CustomApplicationAggregation[]
  readonly loadingAppArchitecture: boolean
  readonly loadingAppArchitectureComponents: boolean
  readonly loadingAssociatedApps: boolean
  readonly loadingAggregations: boolean
}

interface Props<TAggregationCategory extends string> {
  readonly applicationID: string
  readonly aggregationFilters: AggregationOptions<TAggregationCategory>
  readonly additionalFilters?: { readonly [key: string]: any }

  readonly children: (
    props: ChildProps<TAggregationCategory>
  ) => React.ReactElement
}

// TODO flowmap - add tests CONTRAST-37856
export function FlowMapLoader<TAggregationCategory extends string>({
  applicationID,
  aggregationFilters,
  additionalFilters,
  children
}: Props<TAggregationCategory>) {
  return (
    <ViewerLoader>
      {({ viewer, loading }) => {
        return (
          !loading &&
          !!viewer && (
            <ApplicationArchitectureLoader
              applicationID={applicationID}
              organizationID={viewer.currentOrganization.id}
            >
              {({
                applicationArchitecture,
                loading: loadingAppArchitecture
              }) => {
                return (
                  <ApplicationArchitectureComponentsLoader
                    applicationID={applicationID}
                    organizationID={viewer.currentOrganization.id}
                  >
                    {({
                      applicationArchitectureComponents,
                      loading: loadingAppArchitectureComponents
                    }) => {
                      let childProps: ChildProps<TAggregationCategory> = {
                        applicationArchitecture,
                        applicationArchitectureComponents,
                        architectureAssociatedApplications: [],
                        organizationID: viewer.currentOrganization.id,
                        organizationRole: viewer.currentOrganization.role,
                        organizationType: viewer.currentOrganization.type,
                        loadingAppArchitecture,
                        loadingAppArchitectureComponents,
                        loadingAssociatedApps: true,
                        loadingAggregations: false
                      }

                      return !loadingAppArchitectureComponents &&
                        Array.isArray(applicationArchitectureComponents) &&
                        applicationArchitectureComponents.length > 0 ? (
                        <ArchitectureAssociatedApplicationsLoader
                          applicationID={applicationID}
                          organizationID={viewer.currentOrganization.id}
                          componentHashes={applicationArchitectureComponents.map(
                            (component) => component.hash
                          )}
                          aggregationFilters={{ ...aggregationFilters }}
                          additionalFilters={additionalFilters}
                        >
                          {({
                            architectureAssociatedApplications,
                            loading: loadingAssociatedApps
                          }) => {
                            childProps = {
                              ...childProps,
                              architectureAssociatedApplications,
                              loadingAssociatedApps
                            }

                            return Array.isArray(
                              architectureAssociatedApplications
                            ) &&
                              architectureAssociatedApplications.length > 0 ? (
                              <AggregationsLoader
                                queries={[
                                  getArchitectureAssociatedAppAggregationsQuery(
                                    applicationID
                                  )
                                ]}
                                queryRootKey="architectureAssociatedApplicationAggregations"
                                organizationID={viewer.currentOrganization.id}
                                aggregationFilters={{
                                  ...aggregationFilters,
                                  apps: architectureAssociatedApplications.map(
                                    (associatedApp) =>
                                      associatedApp.application.id
                                  )
                                }}
                                additionalFilters={additionalFilters}
                              >
                                {({
                                  aggregations,
                                  loading: loadingAggregations
                                }) => {
                                  childProps = {
                                    ...childProps,
                                    aggregations,
                                    loadingAggregations
                                  }

                                  return (
                                    <MetadataAggregationsLoader
                                      query={getCustomArchitectureAssociatedApplicationAggregationsQuery(
                                        applicationID
                                      )}
                                      organizationID={
                                        viewer.currentOrganization.id
                                      }
                                      aggregationFilters={{
                                        ...aggregationFilters,
                                        apps: architectureAssociatedApplications.map(
                                          (app) => app.application.id
                                        )
                                      }}
                                      additionalFilters={additionalFilters}
                                    >
                                      {({
                                        customMetadataAggregations:
                                          metadataAggregations
                                      }) => {
                                        return children({
                                          ...childProps,
                                          metadataAggregations
                                        })
                                      }}
                                    </MetadataAggregationsLoader>
                                  )
                                }}
                              </AggregationsLoader>
                            ) : (
                              children(childProps)
                            )
                          }}
                        </ArchitectureAssociatedApplicationsLoader>
                      ) : (
                        children({
                          ...childProps,
                          loadingAssociatedApps: false
                        })
                      )
                    }}
                  </ApplicationArchitectureComponentsLoader>
                )
              }}
            </ApplicationArchitectureLoader>
          )
        )
      }}
    </ViewerLoader>
  )
}
